// web/shims/KeyboardControllerShim.tsx
// Shim for react-native-keyboard-controller on web

import React from 'react';
import { ScrollView, ScrollViewProps, View, ViewProps } from 'react-native';

// On web, keyboard handling is automatic, so just use a regular ScrollView
export const KeyboardAwareScrollView = React.forwardRef<
  ScrollView,
  ScrollViewProps & { bottomOffset?: number }
>(({ bottomOffset, ...props }, ref) => <ScrollView ref={ref} {...props} />);

KeyboardAwareScrollView.displayName = 'KeyboardAwareScrollView';

// KeyboardStickyView - just renders children in a View on web
export const KeyboardStickyView = React.forwardRef<
  View,
  ViewProps & { offset?: { opened?: number; closed?: number } }
>(({ offset, ...props }, ref) => <View ref={ref} {...props} />);

KeyboardStickyView.displayName = 'KeyboardStickyView';

// KeyboardAvoidingView shim
export const KeyboardAvoidingView = React.forwardRef<
  View,
  ViewProps & { behavior?: string; keyboardVerticalOffset?: number }
>(({ behavior, keyboardVerticalOffset, ...props }, ref) => (
  <View ref={ref} {...props} />
));

KeyboardAvoidingView.displayName = 'KeyboardAvoidingView';

// Stub other exports that might be used
export const KeyboardController = {
  setInputMode: () => {},
  setDefaultMode: () => {},
  dismiss: () => {},
};

export const KeyboardProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => <>{children}</>;

export const useKeyboardHandler = () => {};
export const useKeyboardAnimation = () => ({
  height: { value: 0 },
  progress: { value: 0 },
});
export const useReanimatedKeyboardAnimation = () => ({
  height: { value: 0 },
  progress: { value: 0 },
});
export const useKeyboardContext = () => ({ isVisible: false, height: 0 });
export const useKeyboardState = <T,>(
  selector?: (state: { isVisible: boolean; height: number }) => T,
) => {
  const state = { isVisible: false, height: 0 };
  return selector ? selector(state) : (state as T);
};

export default {
  KeyboardAwareScrollView,
  KeyboardStickyView,
  KeyboardAvoidingView,
  KeyboardController,
  KeyboardProvider,
  useKeyboardHandler,
  useKeyboardAnimation,
  useReanimatedKeyboardAnimation,
  useKeyboardContext,
  useKeyboardState,
};
